import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import COLORS from "../theme";

import { getAllAssignedTasks, updateSchedule } from '../utils/api';

const HOUR_HEIGHT = 240; // px per hour (change to taste)
const VISIBLE_HOURS = 5; // Show 5 hours at a time
const DAY_START_MIN = 0; // 00:00
const DAY_END_MIN = 24 * 60; // 1440
const totalDayHeight = ((DAY_END_MIN - DAY_START_MIN) / 60) * HOUR_HEIGHT;

function hhmmToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function minutesFromDayStart(hhmm) {
  return hhmmToMinutes(hhmm) - DAY_START_MIN;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

// ====== TASK BLOCK COMPONENT ======
function TaskBlock({ task, onClick }) {
  const top = task.dayOffset + (minutesFromDayStart(task.start) / 60) * HOUR_HEIGHT;
  // Fix: Use 3600 seconds per hour, not 60
  const height = (task.duration / 3600) * HOUR_HEIGHT;

  return (
    <Box
      onClick={() => onClick?.(task)}
      role="button"
      tabIndex={0}
      sx={{
        position: "absolute",
        left: { xs: 76, sm: 100 }, // leave room for hour labels
        right: 12,
        top,
        height: clamp(height, 24, HOUR_HEIGHT),
        bgcolor: "secondary.main",
        color: "#000",
        borderRadius: 8,
        boxShadow: `0 0 0 2px ${COLORS.white}22, 0 8px 20px rgba(0,0,0,.35)`,
        cursor: "pointer",
        p: 1.25,
        outline: "none",
        "&:hover": {
          filter: "brightness(1.08)",
          boxShadow: `0 0 0 2px ${COLORS.white}55, 0 10px 24px rgba(0,0,0,.45)`,
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        borderColor:"primary.main",
        borderWidth: 2,
        borderStyle: "solid",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{alignContent:"center", justifyContent:"center"}}
      >
        {task.title}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {task.category_color && (
          <Box
            aria-hidden="true"
            sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: task.category_color }}
          />
        )}
        <Typography variant="caption">
          {task.category === 'no-category' ? 'No category' : task.category}
        </Typography>
      </Stack>
    </Box>
  );
}

// ====== CURRENT TIME LINE ======
function NowLine({ currentDayOffset = 0 }) {
  const [currentTime, setCurrentTime] = React.useState(new Date());

  React.useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const top = currentDayOffset + (clamp(currentMinutes, DAY_START_MIN, DAY_END_MIN) / 60) * HOUR_HEIGHT;

  return (
    <Box
      aria-label="Current time indicator"
      sx={{
        position: "absolute",
        left: { xs: 72, sm: 96 },
        right: 8,
        top,
        height: 0,
        borderTop: `2px dashed`,
        borderColor: "green.main",
        filter: "drop-shadow(0 0 6px rgba(255,45,149,0.6))",
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -10,
          top: -6,
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: "secondary.main",
          boxShadow: "0 0 10px rgba(255,45,149,0.9)",
        }}
      />
    </Box>
  );
}

// ====== HOUR GRID / LABELS ======
function HoursBackdrop({ totalDays }) {
  const hours = Array.from({ length: 25 }, (_, i) => i); // 0..24 ticks

  return (
    <React.Fragment>
      {/* hour lines and labels for all days */}
      {Array.from({ length: totalDays }, (_, dayIndex) => (
        <React.Fragment key={`day-${dayIndex}`}>
          {/* hour lines for this day */}
          {hours.map((h) => (
            <Box
              key={`line-${dayIndex}-${h}`}
              sx={{
                position: "absolute",
                left: { xs: 72, sm: 96 },
                right: 8,
                top: dayIndex * totalDayHeight + h * HOUR_HEIGHT,
                height: 0,
                border: "1px solid",
                borderColor:"green.main",
              }}
            />
          ))}
          {/* hour labels for this day */}
          {hours.slice(0, 24).map((h) => (
            <Typography
              key={`label-${dayIndex}-${h}`}
              variant="caption"
              sx={{
                position: "absolute",
                left: 8,
                top: dayIndex * totalDayHeight + h * HOUR_HEIGHT - 6,
                width: { xs: 60, sm: 80 },
                textAlign: "right",
                color: `${COLORS.white}CC`,
                fontWeight: 700,
              }}
            >
              {String(h).padStart(2, "0")}:00
            </Typography>
          ))}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
}

// ====== MAIN COMPONENT ======
function DaySchedule() {
  const [allTasks, setAllTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [updating, setUpdating] = React.useState(false);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const [dateRange, setDateRange] = React.useState({ start: null, end: null });
  const [reloading, setReloading] = React.useState(false);
  const scrollContainerRef = React.useRef(null);

  const navigate = useNavigate();

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };





  // Helper function to calculate task display properties
  const processTaskForDisplay = (task) => {
    if (!task.start_time) {
      console.log('Task missing start_time:', task);
      return {
        ...task,
        displayStart: 0,
        displayDuration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
        start: '00:00',
        duration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
      };
    }

    try {
      // Parse the start_time directly - use as-is for positioning
      const startDate = new Date(task.start_time);

      // Check if parsing succeeded
      if (isNaN(startDate.getTime())) {
        console.error('Invalid start_time for task:', task.title, task.start_time);
        return {
          ...task,
          displayStart: 0,
          displayDuration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
          start: '00:00',
          duration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
        };
      }

      // Calculate minutes from start of day
      const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
      const displayDuration = Math.max(0, (task.duration || 300) - (task.working_time || 0));

      console.log('Task positioning:', {
        title: task.title,
        start_time: task.start_time,
        parsedDate: startDate.toISOString(),
        startMinutes: startMinutes,
        hours: startDate.getHours(),
        minutes: startDate.getMinutes()
      });

      return {
        ...task,
        displayStart: startMinutes,
        displayDuration: displayDuration,
        start: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
        duration: displayDuration,
        startDate, // Keep original date for reference
      };
    } catch (error) {
      console.error('Error processing task for display:', task, error);
      return {
        ...task,
        displayStart: 0,
        displayDuration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
        start: '00:00',
        duration: Math.max(0, (task.duration || 300) - (task.working_time || 0)),
      };
    }
  };

  // Load tasks from API with date range
  const loadTasks = React.useCallback(async (start, end) => {
    setReloading(true);
    try {
      const filters = {
        completed: 'false',
        has_start_time: 'true',
        start,
        end
      };

      let page = 1;
      let allTasks = [];
      while (true) {
        // Don't send timezone - backend now returns UTC times
        const response = await getAllAssignedTasks(page, 100, filters);
        allTasks = allTasks.concat(response.tasks || []);
        if (!response.pagination?.has_next) break;
        page++;
      }
      console.log('Sample tasks with start_time:', allTasks.filter(task => task.start_time).slice(0, 3));
      setAllTasks(allTasks);

      // Calculate date range from loaded tasks
      if (allTasks.length > 0) {
        const dates = allTasks
          .map(task => task.start_time ? new Date(task.start_time) : null)
          .filter(date => date !== null)
          .sort((a, b) => a - b);
        const minDate = dates[0];
        const maxDate = dates[dates.length - 1];
        const minDateStr = minDate.toISOString();
        const maxDateStr = new Date(maxDate);
        maxDateStr.setHours(23, 59, 59, 999);
        const maxDateStrIso = maxDateStr.toISOString();
        return { tasks: allTasks, dateRange: { start: minDateStr, end: maxDateStrIso } };
      }
      return { tasks: allTasks, dateRange: { start, end } };
    } catch (error) {
      console.error('Failed to fetch assigned tasks:', error);
      return { tasks: [], dateRange: { start, end } };
    } finally {
      setReloading(false);
    }
  }, []);

  // Handle update schedule
  const handleUpdateSchedule = async () => {
    setUpdating(true);
    try {
      await updateSchedule();
      // Reload tasks after update
      await loadTasks(dateRange.start, dateRange.end);
    } catch (error) {
      console.error('Failed to update schedule:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Handle task click
  const handleTaskClick = (task) => {
    navigate('/task-view', { state: { taskId: task.task_id } });
  };

  // Initial load
  React.useEffect(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setDate(end.getDate() + 1);
    end.setHours(23, 59, 59, 999);
    const initialStart = start.toISOString();
    const initialEnd = end.toISOString();

    const fetchInitialTasks = async () => {
      setLoading(true);
      const result = await loadTasks(initialStart, initialEnd);
      if (result.dateRange) {
        setDateRange(result.dateRange);
      }
      setLoading(false);
    };

    fetchInitialTasks();
  }, [loadTasks]);

  // Reload tasks when date range changes
  React.useEffect(() => {
    if (dateRange.start && dateRange.end) {
      loadTasks(dateRange.start, dateRange.end);
    }
  }, [dateRange, loadTasks]);

  // Initial scroll positioning to current day
  React.useEffect(() => {
    if (scrollContainerRef.current && allTasks.length > 0 && !loading) {
      // Calculate unique dates for positioning
      const dates = allTasks
        .map(task => task.start_time ? new Date(task.start_time) : null)
        .filter(date => date !== null);

      const uniqueDateObjects = [...new Set(dates.map(date => date.getTime()))]
        .map(time => new Date(time))
        .sort((a, b) => a - b);

      const uniqueDateStrings = uniqueDateObjects.map(date => date.toDateString());

      if (uniqueDateStrings.length > 0) {
        // Scroll to the current day after tasks are loaded
        const today = new Date();
        const todayString = today.toDateString();
        const todayIndex = uniqueDateStrings.findIndex(date => date === todayString);
        const targetIndex = todayIndex >= 0 ? todayIndex : 0;
        const targetScrollTop = targetIndex * totalDayHeight;

        // Only scroll if we're not already at the correct position
        if (Math.abs(scrollContainerRef.current.scrollTop - targetScrollTop) > 10) {
          scrollContainerRef.current.scrollTop = targetScrollTop;
        }
      }
    }
  }, [allTasks, loading]);





  // Calculate current view date based on scroll position and date range
  const currentViewDate = React.useMemo(() => {
    if (!allTasks.length) {
      return new Date();
    }

    // Calculate unique dates inline to avoid use-before-define
    const dates = allTasks
      .map(task => task.start_time ? new Date(task.start_time) : null)
      .filter(date => date !== null);

    const uniqueDateObjects = [...new Set(dates.map(date => date.getTime()))]
      .map(time => new Date(time))
      .sort((a, b) => a - b);

    const uniqueDateStrings = uniqueDateObjects.map(date => date.toDateString());

    const dayIndex = Math.floor(scrollPosition / totalDayHeight);
    const maxDayIndex = uniqueDateStrings.length - 1;
    const clampedDayIndex = Math.min(dayIndex, maxDayIndex);

    if (uniqueDateStrings.length > 0 && clampedDayIndex >= 0) {
      const currentDate = new Date(uniqueDateStrings[clampedDayIndex]);
      return currentDate;
    }

    return new Date();
  }, [scrollPosition, allTasks]);

  // Get all tasks with their day positions for multi-day view
  const allTasksWithDays = React.useMemo(() => {
    if (!allTasks.length) return [];

    console.log('Processing tasks for multi-day view, total tasks:', allTasks.length);

    // Group tasks by date and process them
    const tasksByDate = {};
    const processedTasks = [];

    allTasks.forEach(task => {
      if (!task.start_time) {
        console.log('Task missing start_time:', task);
        return;
      }

      const taskDate = new Date(task.start_time);
      const dateString = taskDate.toDateString();

      if (!tasksByDate[dateString]) {
        tasksByDate[dateString] = [];
      }
      tasksByDate[dateString].push(task);
    });

    console.log('Tasks grouped by date:', tasksByDate);

    // Process tasks and add day offset
    Object.keys(tasksByDate).forEach((dateString, index) => {
      const dayTasks = tasksByDate[dateString];
      const dayOffset = index * totalDayHeight;

      console.log(`Processing day ${index}: ${dateString}, offset: ${dayOffset}px, tasks: ${dayTasks.length}`);

      dayTasks.forEach(task => {
        const processedTask = processTaskForDisplay(task);
        const finalTask = {
          ...processedTask,
          dayOffset,
          dateString
        };

        console.log('Final task:', {
          id: finalTask.id,
          title: finalTask.title,
          dateString: finalTask.dateString,
          dayOffset: finalTask.dayOffset,
          start: finalTask.start,
          duration: finalTask.duration
        });

        processedTasks.push(finalTask);
      });
    });

    console.log('Total processed tasks:', processedTasks.length);
    return processedTasks;
  }, [allTasks]);

  // Handle scroll events for day tracking and updating date range
  React.useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const currentScrollTop = scrollContainerRef.current.scrollTop;
        setScrollPosition(currentScrollTop);

        // Calculate current day index based on scroll position
        const dayIndex = Math.floor(currentScrollTop / totalDayHeight);

        // Calculate unique dates inline to avoid use-before-define
        if (allTasks.length > 0) {
          const dates = allTasks
            .map(task => task.start_time ? new Date(task.start_time) : null)
            .filter(date => date !== null);

          const uniqueDateObjects = [...new Set(dates.map(date => date.getTime()))]
            .map(time => new Date(time))
            .sort((a, b) => a - b);

          const uniqueDateStrings = uniqueDateObjects.map(date => date.toDateString());

          // Only update if we have dates and the day index is valid
          if (uniqueDateStrings.length > 0 && dayIndex >= 0 && dayIndex < uniqueDateStrings.length) {
            // Get the current date being viewed
            const currentViewDate = new Date(uniqueDateStrings[dayIndex]);

            // Calculate new date range: one day before and one day after current view date
            const newStart = new Date(currentViewDate);
            newStart.setDate(newStart.getDate() - 1);
            newStart.setHours(0, 0, 0, 0);

            const newEnd = new Date(currentViewDate);
            newEnd.setDate(newEnd.getDate() + 1);
            newEnd.setHours(23, 59, 59, 999);

            const newStartStr = newStart.toISOString();
            const newEndStr = newEnd.toISOString();

            // Update date range if changed
            if (newStartStr !== dateRange.start || newEndStr !== dateRange.end) {
              setDateRange({ start: newStartStr, end: newEndStr });
            }
          }
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [dateRange, allTasks]);

  // Get unique dates for day headers (sorted chronologically)
  const uniqueDates = React.useMemo(() => {
    if (!allTasks.length) return [];

    const dates = allTasks
      .map(task => task.start_time ? new Date(task.start_time) : null)
      .filter(date => date !== null);

    // Remove duplicates and sort chronologically
    const uniqueDateObjects = [...new Set(dates.map(date => date.getTime()))]
      .map(time => new Date(time))
      .sort((a, b) => a - b);

    const uniqueDateStrings = uniqueDateObjects.map(date => date.toDateString());

    console.log('Chronologically sorted dates:', uniqueDateStrings);

    return uniqueDateStrings;
  }, [allTasks]);

  // Calculate current day offset for the NowLine
  const currentDayOffset = React.useMemo(() => {
    if (!allTasks.length) return 0;

    // Calculate unique dates inline to avoid use-before-define
    const dates = allTasks
      .map(task => task.start_time ? new Date(task.start_time) : null)
      .filter(date => date !== null);

    const uniqueDateObjects = [...new Set(dates.map(date => date.getTime()))]
      .map(time => new Date(time))
      .sort((a, b) => a - b);

    const uniqueDateStrings = uniqueDateObjects.map(date => date.toDateString());

    if (!uniqueDateStrings.length) return 0;

    const today = new Date();
    const todayString = today.toDateString();

    console.log('Today:', todayString);
    console.log('Available dates:', uniqueDateStrings);

    // Find today's index in the schedule
    const todayIndex = uniqueDateStrings.findIndex(date => {
      console.log('Comparing:', date, 'with', todayString, 'equals:', date === todayString);
      return date === todayString;
    });

    console.log('Today index:', todayIndex);

    // If today is not in the schedule, use the first day
    const targetIndex = todayIndex >= 0 ? todayIndex : 0;

    const offset = targetIndex * totalDayHeight;
    console.log('Current day offset:', offset, 'targetIndex:', targetIndex);

    return offset;
  }, [allTasks]);

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden' }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 920,
          mx: "auto",
          my: 0,
          p: 2,
          position: "relative",
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background:
            "radial-gradient(1000px 280px at 0% -20%, rgba(0,229,255,0.12), transparent 60%), radial-gradient(800px 240px at 100% 120%, rgba(255,45,149,0.12), transparent 60%), #0F1513",
        }}
      >
        {/* Fixed Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
            {formatDateForDisplay(currentViewDate)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Update Schedule">
              <IconButton
                color="secondary"
                onClick={handleUpdateSchedule}
                disabled={updating}
              >
                {updating ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 1.5, borderColor: `${COLORS.white}22` }} />

        {/* Scrollable Timeline Container */}
        <Box
          ref={(el) => {
            if (el && el !== scrollContainerRef.current) {
              scrollContainerRef.current = el;
            }
          }}
          sx={{
            position: "relative",
            height: VISIBLE_HOURS * HOUR_HEIGHT,
            overflow: 'auto',
            borderLeft: `2px solid ${COLORS.white}22`,
            borderRight: `2px solid ${COLORS.white}12`,
            borderRadius: 2,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: uniqueDates.length * totalDayHeight,
              minHeight: '100%',
            }}
          >
            <HoursBackdrop totalDays={uniqueDates.length} />
            <NowLine currentDayOffset={currentDayOffset} />

            {/* All tasks across all days */}
            {allTasksWithDays.map((task) => (
              <TaskBlock key={`${task.id}-${task.dateString}`} task={task} onClick={handleTaskClick} />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Loading indicator for more tasks */}
      {(loading || reloading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export default DaySchedule;
